/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useRef, useState } from "react";
import "./App.scss";
import { LiveAPIProvider } from "./contexts/LiveAPIContext";
import SidePanel from "./components/side-panel/SidePanel";
import { SchedulingAgent } from "./components/receptionist/outbound_agent";
import ControlTray from "./components/control-tray/ControlTray";
import { ClientSelection } from "./components/receptionist/client-selection";
import cn from "classnames";
import { LiveClientOptions } from "./types";

interface Client {
  id: string;
  name: string;
  dateOfBirth: string;
  phone: string;
  email: string;
  lastVisit: {
    date: string;
    services: string[];
    stylist: string;
    totalCost: number;
    notes?: string;
  };
}

const API_KEY = "AIzaSyBE_64DxDW9u4OwabYvF5pExk7uFx4BfU0"
if (typeof API_KEY !== "string") {
  throw new Error("set REACT_APP_GEMINI_API_KEY in .env");
}

const apiOptions: LiveClientOptions = {
  apiKey: API_KEY,
};

function App() {
  // this video reference is used for displaying the active stream, whether that is the webcam or screen capture
  const videoRef = useRef<HTMLVideoElement>(null);
  // either the screen capture, the video or null, if null we hide it
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);
  // client selection state
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [showClientSelection, setShowClientSelection] = useState(true);

  const handleClientSelect = (client: Client) => {
    setSelectedClient(client);
    setShowClientSelection(false);
  };

  const handleBackToClientSelection = () => {
    setShowClientSelection(true);
    setSelectedClient(null);
  };

  // Show client selection screen
  if (showClientSelection) {
    return (
      <div className="App">
        <ClientSelection onClientSelect={handleClientSelect} />
      </div>
    );
  }

  // Show main scheduling interface
  return (
    <div className="App">
      <LiveAPIProvider options={apiOptions}>
        <div className="streaming-console">
          <SidePanel />
          <main className="main-content">
            <div className="main-app-area">
              <div className="client-info-header">
                <div className="selected-client-display">
                  <span className="client-label">Conversation with:</span>
                  <span className="client-name">{selectedClient?.name}</span>
                  <button 
                    className="back-to-selection-btn"
                    onClick={handleBackToClientSelection}
                    title="Back to client selection"
                  >
                    ← Select Different Client
                  </button>
                </div>
              </div>
              <SchedulingAgent selectedClient={selectedClient} />
              {/* Video Stream Display */}
              {videoStream && (
                <div className="video-container">
                  <video
                    className="stream"
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                  />
                </div>
              )}
            </div>

            <ControlTray
              videoRef={videoRef}
              supportsVideo={true}
              onVideoStreamChange={setVideoStream}
              enableEditingSettings={true}
            />
          </main>
        </div>
      </LiveAPIProvider>
    </div>
  );
}

export default App;
